'use client'

import { useMemo } from 'react'
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts'
import { 
  ActivityDataPoint, 
  CategoryCount,
} from '../types'
import { getSentimentColorValue, getCategoryColor } from '../utils/styleUtils'

interface PercentChartProps {
  activityData: ActivityDataPoint[]
  categoryData: CategoryCount[]
  brandData: CategoryCount[]
  sourceData: CategoryCount[]
  breakdownType: 'sentiment' | 'category' | 'brand' | 'source'
  categoriesByPeriod: { [period: string]: CategoryCount[] }
  brandsByPeriod: { [period: string]: CategoryCount[] }
  sourcesByPeriod: { [period: string]: CategoryCount[] }
  onSentimentClick?: (sentiment: string) => void
  onCategoryClick?: (category: string) => void
  onBrandClick?: (brand: string) => void
  onSourceClick?: (source: string) => void
}

const toPercent = (decimal: number) => `${Math.round(decimal * 100)}%`

const getPercent = (value: number, total: number) => {
  const ratio = total > 0 ? value / total : 0
  return toPercent(ratio)
}

const renderTooltipContent = (o: any) => {
  const { payload, label } = o
  if (!payload || payload.length === 0) return null
  
  const total = payload.reduce((result: number, entry: any) => result + entry.value, 0)

  return (
    <div className="bg-white p-3 border border-gray-200 shadow-md rounded-md text-xs">
      <p className="font-medium mb-2">{`${label} (Total: ${total})`}</p>
      <ul className="list-none m-0 p-0">
        {payload.map((entry: any, index: number) => (
          <li key={`item-${index}`} className="flex items-center justify-between py-1">
            <div className="flex items-center">
              <div 
                className="w-2 h-2 mr-1 rounded-sm" 
                style={{ backgroundColor: entry.color }}
              />
              <span>{entry.name}:</span>
            </div>
            <span className="ml-2 font-medium">{`${entry.value} (${getPercent(entry.value, total)})`}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

export function PercentChart({
  activityData,
  categoryData,
  brandData,
  sourceData,
  breakdownType,
  categoriesByPeriod,
  brandsByPeriod,
  sourcesByPeriod,
  onSentimentClick,
  onCategoryClick,
  onBrandClick,
  onSourceClick
}: PercentChartProps) {
  // Preprocess data for the percent chart
  const chartData = useMemo(() => {
    return activityData.map(item => {
      const result: any = {
        period: item.label,
      }
      
      if (breakdownType === 'sentiment') {
        // Add sentiment data
        Object.entries(item.sentiments).forEach(([sentiment, count]) => {
          result[sentiment] = count || 0
        })
      } else if (breakdownType === 'category') {
        // Add category data
        const periodCategories = categoriesByPeriod[item.label] || []
        periodCategories.forEach(cat => {
          result[cat.category] = cat.count
        })
      } else if (breakdownType === 'brand') {
        // Add brand data
        const periodBrands = brandsByPeriod[item.label] || []
        periodBrands.forEach(brand => {
          result[brand.category] = brand.count
        })
      } else if (breakdownType === 'source') {
        // Add source data
        const periodSources = sourcesByPeriod[item.label] || []
        periodSources.forEach(source => {
          result[source.category] = source.count
        })
      }
      
      return result
    })
  }, [activityData, breakdownType, categoriesByPeriod, brandsByPeriod, sourcesByPeriod])

  // Get colors and data keys based on breakdown type
  const { dataKeys, colors } = useMemo(() => {
    let keys: string[] = []
    let colorMap: {[key: string]: string} = {}
    
    if (breakdownType === 'sentiment') {
      // Get unique sentiment keys across all periods
      activityData.forEach(item => {
        Object.entries(item.sentiments).forEach(([sentiment]) => {
          if (!keys.includes(sentiment)) {
            keys.push(sentiment)
          }
        })
      })
      // Map sentiment colors
      keys.forEach(key => {
        colorMap[key] = getSentimentColorValue(key)
      })
    } else if (breakdownType === 'category') {
      // Get the top categories across all periods to limit the chart
      const allCategories = Object.values(categoriesByPeriod)
        .flatMap(categories => categories)
      
      // Get unique categories and sort by total count
      const uniqueCategories = Array.from(new Set(allCategories.map(cat => cat.category)))
        .map(catName => {
          const total = allCategories
            .filter(cat => cat.category === catName)
            .reduce((sum, cat) => sum + cat.count, 0)
          return { category: catName, total }
        })
        .sort((a, b) => b.total - a.total)
        .slice(0, 10) // Limit to top 10 categories
      
      keys = uniqueCategories.map(cat => cat.category)
        .filter(category => category !== 'Uncategorized') // Filter out Uncategorized
      
      // Map category colors
      keys.forEach((key, index) => {
        const catItem = categoryData.find(cat => cat.category === key)
        colorMap[key] = catItem?.color || getCategoryColor(key, index)
      })
    } else if (breakdownType === 'brand') {
      // Similar approach for brands
      const allBrands = Object.values(brandsByPeriod)
        .flatMap(brands => brands)
      
      const uniqueBrands = Array.from(new Set(allBrands.map(brand => brand.category)))
        .map(brandName => {
          const total = allBrands
            .filter(brand => brand.category === brandName)
            .reduce((sum, brand) => sum + brand.count, 0)
          return { category: brandName, total }
        })
        .sort((a, b) => b.total - a.total)
        .slice(0, 10) // Limit to top 10 brands
      
      keys = uniqueBrands.map(brand => brand.category)
        .filter(brand => brand !== 'Uncategorized' && brand !== 'Unknown Brand') // Filter out Uncategorized
      
      // Map brand colors
      keys.forEach((key, index) => {
        const brandItem = brandData.find(brand => brand.category === key)
        colorMap[key] = brandItem?.color || getCategoryColor(key, index)
      })
    } else if (breakdownType === 'source') {
      // Similar approach for sources
      const allSources = Object.values(sourcesByPeriod)
        .flatMap(sources => sources)
      
      const uniqueSources = Array.from(new Set(allSources.map(source => source.category)))
        .map(sourceName => {
          const total = allSources
            .filter(source => source.category === sourceName)
            .reduce((sum, source) => sum + source.count, 0)
          return { category: sourceName, total }
        })
        .sort((a, b) => b.total - a.total)
        .slice(0, 10) // Limit to top 10 sources
      
      keys = uniqueSources.map(source => source.category)
        .filter(source => source !== 'Uncategorized' && source !== 'Unknown Source') // Filter out Uncategorized
      
      // Map source colors
      keys.forEach((key, index) => {
        const sourceItem = sourceData.find(source => source.category === key)
        colorMap[key] = sourceItem?.color || getCategoryColor(key, index)
      })
    }
    
    return { dataKeys: keys, colors: colorMap }
  }, [activityData, breakdownType, categoryData, brandData, sourceData, categoriesByPeriod, brandsByPeriod, sourcesByPeriod])

  // Add error checking for empty periods
  const processedChartData = useMemo(() => {
    // Make sure all keys are present in all periods
    return chartData.map(periodData => {
      const result = { ...periodData };
      
      // Add missing keys with zero values
      dataKeys.forEach(key => {
        if (result[key] === undefined) {
          result[key] = 0;
        }
      });
      
      return result;
    });
  }, [chartData, dataKeys]);

  // Handle click events based on breakdown type
  const handleClick = (entry: any) => {
    const { dataKey } = entry
    
    if (breakdownType === 'sentiment' && onSentimentClick) {
      onSentimentClick(dataKey)
    } else if (breakdownType === 'category' && onCategoryClick) {
      onCategoryClick(dataKey)
    } else if (breakdownType === 'brand' && onBrandClick) {
      onBrandClick(dataKey)
    } else if (breakdownType === 'source' && onSourceClick) {
      onSourceClick(dataKey)
    }
  }

  return (
    <div className="h-full w-full relative min-h-[320px]">
      <ResponsiveContainer width="100%" height={320} minHeight={320}>
        <AreaChart
          data={processedChartData}
          stackOffset="expand"
          margin={{
            top: 10,
            right: 15,
            left: -25,
            bottom: 0,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis 
            dataKey="period" 
            tick={{ fontSize: 8, fill: '#4B5563' }}
            angle={-45}
            textAnchor="end"
            height={60}
            tickMargin={10}
            interval={0}
            tickFormatter={(value: string) => {
              // For mobile, try to make labels even shorter
              if (typeof window !== 'undefined' && window.innerWidth < 640) {
                if (value && value.startsWith('Week ') && value.includes('(')) {
                  const match = value.match(/Week \d+ \((\d+)\/(\d+)\)/);
                  if (match) {
                    return `${match[2]}/${match[1]}`; // Just day/month format for mobile
                  }
                }
                // Shorten month names on mobile
                if (value && ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].some(month => value.includes(month))) {
                  const months = {
                    'January': 'Jan', 'February': 'Feb', 'March': 'Mar', 'April': 'Apr',
                    'May': 'May', 'June': 'Jun', 'July': 'Jul', 'August': 'Aug',
                    'September': 'Sep', 'October': 'Oct', 'November': 'Nov', 'December': 'Dec'
                  };
                  Object.entries(months).forEach(([full, abbr]) => {
                    value = value.replace(full, abbr);
                  });
                  return value;
                }
              }
              
              if (value && value.startsWith('Week ') && value.includes('(')) {
                // For weekly segmentation, just show the date in DD/MM/YY format
                const match = value.match(/Week \d+ \((\d+)\/(\d+)\)/);
                if (match) {
                  const month = match[1];
                  const day = match[2];
                  const year = new Date().getFullYear().toString().substr(2, 2); // Get last 2 digits of current year
                  return `${day}/${month}/${year}`; // Return as DD/MM/YY
                }
              }
              return value;
            }}
          />
          <YAxis 
            tickFormatter={toPercent} 
            tick={{ fontSize: 8, fill: '#4B5563' }}
            domain={[0, 1]}
            axisLine={false}
          />
          <Tooltip content={renderTooltipContent} />
          <Legend wrapperStyle={{ fontSize: '12px', paddingBottom: 0 }} />
          {dataKeys.map((key, index) => (
            <Area
              key={key}
              type="monotone"
              dataKey={key}
              name={key}
              stackId="1"
              stroke={colors[key] || getCategoryColor(key, index)}
              fill={colors[key] || getCategoryColor(key, index)}
              onClick={() => handleClick({ dataKey: key })}
              style={{ cursor: 'pointer' }}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  )
} 